/**
 * FooterContainer - Main Footer entry point
 *
 * Wraps footer content with all necessary providers:
 * - QueryClientProvider (from Infrastructure via Frontend/Theme)
 * - UiProvider (from Frontend/Theme for UI variants)
 *
 * Newsletter uses React 19 useActionState pattern directly in hook,
 * no longer requires FooterProvider context.
 *
 * React 19 + TanStack Query v5 patterns.
 */

import { QueryClientProvider } from '@tanstack/react-query'

import { FooterLinks, FooterInfo, FooterCopyright, LegalGuaranteeNotice } from './info'
import { Newsletter } from './newsletter'
import {
  CustomerSupport,
  GeneralInfo,
  UsefulLinks,
  AboutUs,
} from './sections'
import { getQueryClient } from '../../../Infrastructure'
import { Separator, UiProvider } from '../../Theme'


// Sections

// Newsletter

// Info

/**
 * FooterContainerProps
 *
 * `legalGuaranteeImageUrl` is optional for backward compatibility with
 * mount points that don't pass it (no broken image when absent).
 */
export interface FooterContainerProps {
  legalGuaranteeImageUrl?: string
}

/**
 * FooterContent - Footer layout with all sections
 */
function FooterContent({ legalGuaranteeImageUrl }: FooterContainerProps) {
  return (
    <>
      <div className="w-full bg-gray-100 flex items-center justify-center">
        <Newsletter />
      </div>

      {/* Separator between newsletter and footer sections */}
      <Separator className="bg-gray-300" />

      <div className="flex flex-col gap-10 w-full bg-gray-900 items-center justify-center pt-5 pb-10 px-5">
        <div className="flex flex-row flex-wrap md:flex-nowrap items-start justify-between text-white w-full xl:max-w-screen-xl xl:mx-auto bg-gray-900 text-sm gap-8">
          <div className="w-[calc(50%-1rem)] md:w-auto">
            <CustomerSupport />
          </div>
          <div className="w-[calc(50%-1rem)] md:w-auto">
            <GeneralInfo />
          </div>
          <div className="w-[calc(50%-1rem)] md:w-auto">
            <UsefulLinks />
          </div>
          <div className="w-[calc(50%-1rem)] md:w-auto">
            <AboutUs />
          </div>
          <div className="w-[calc(50%-1rem)] md:w-auto">
            <FooterLinks />
          </div>
        </div>
        {legalGuaranteeImageUrl && (
          <div className="w-full xl:max-w-screen-xl xl:mx-auto">
            <LegalGuaranteeNotice imageUrl={legalGuaranteeImageUrl} />
          </div>
        )}
        <div className="flex flex-col items-start md:flex-row-reverse justify-between md:items-end w-full xl:max-w-screen-xl xl:mx-auto">
          <FooterInfo />
          <FooterCopyright />
        </div>
      </div>
    </>
  )
}

/**
 * FooterContainer - Main footer component with providers
 *
 * Provider hierarchy:
 * 1. QueryClientProvider - React Query client (singleton)
 * 2. UiProvider - UI component variants (from Frontend/Theme)
 *
 * Note: FooterProvider removed - newsletter uses useActionState directly
 */
export function FooterContainer({ legalGuaranteeImageUrl }: FooterContainerProps) {
  const queryClient = getQueryClient()

  return (
    <QueryClientProvider client={queryClient}>
      <UiProvider>
        <FooterContent legalGuaranteeImageUrl={legalGuaranteeImageUrl} />
      </UiProvider>
    </QueryClientProvider>
  )
}
